<template>
    <!-- * 모달 헤더 -->
    <ModalHeader>
        <HeaderContent>
            <HeaderIcon>
                <FileOutline :size="30" />
            </HeaderIcon>
            <HeaderText>
                <ModalTitle>전자세금계산서 & 현금영수증 발행</ModalTitle>
            </HeaderText>
        </HeaderContent>
    </ModalHeader>

    <!-- * 발행 내역 섹션 -->
    <HistorySection>
        <HistoryHeader>
            <HistoryTitle>
                전자세금계산서 & 현금영수증 발행 내역
            </HistoryTitle>
            <HistoryToggleButton @click="toggleHistory">
                <ChevronDown :size="20"
                    :style="{ paddingTop: '3px', transform: isHistoryExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s ease' }" />
                {{ isHistoryExpanded ? '접기' : '펼치기' }}
            </HistoryToggleButton>
        </HistoryHeader>

        <HistoryContent :expanded="isHistoryExpanded">
            <HistoryGrid>
                <!-- 전자세금계산서 내역 (왼쪽) -->
                <HistoryCard>
                    <HistoryCardHeader>
                        <HistoryCardTitle>
                            전자세금계산서
                        </HistoryCardTitle>
                        <HistoryCardActions>
                            <HistoryCardActionButton @click="openTaxInvoicePopup">
                                <Monitor :size="16" :style="{ paddingTop: '2px' }" />
                                세금계산서 문서함
                            </HistoryCardActionButton>
                        </HistoryCardActions>
                    </HistoryCardHeader>

                    <HistoryList v-if="taxInvoiceHistory && taxInvoiceHistory.length > 0">
                        <HistoryItem v-for="(item, index) in taxInvoiceHistory" :key="index">
                            <HistoryItemHeader>
                                <HistoryItemTitle>
                                    {{ item.title }}
                                </HistoryItemTitle>
                                <HistoryItemStatus :status="item.status">
                                    {{ item.statusText }}
                                </HistoryItemStatus>
                            </HistoryItemHeader>

                            <HistoryItemContent>
                                <HistoryItemInfo v-for="(info, infoIndex) in item.details" :key="infoIndex">
                                    <HistoryItemLabel>{{ info.label }}</HistoryItemLabel>
                                    <HistoryItemValue>{{ info.value }}</HistoryItemValue>
                                </HistoryItemInfo>
                            </HistoryItemContent>
                        </HistoryItem>
                    </HistoryList>
                    <HistoryEmpty v-else>
                        아직 발행된 전자세금계산서가 없습니다.
                    </HistoryEmpty>
                </HistoryCard>

                <!-- 현금영수증 내역 (오른쪽) -->
                <HistoryCard>
                    <HistoryCardHeader>
                        <HistoryCardTitle>
                            현금영수증
                        </HistoryCardTitle>
                        <HistoryCardActions>
                            <HistoryCardActionButton @click="openCashReceiptPopup">
                                <Monitor :size="16" :style="{ paddingTop: '2px' }" />
                                현금영수증 문서함
                            </HistoryCardActionButton>
                        </HistoryCardActions>
                    </HistoryCardHeader>

                    <HistoryList v-if="cashReceiptHistory && cashReceiptHistory.length > 0">
                        <HistoryItem v-for="(item, index) in cashReceiptHistory" :key="index">
                            <HistoryItemHeader>
                                <HistoryItemTitle>
                                    {{ item.title }}
                                </HistoryItemTitle>
                                <HistoryItemStatus :status="item.status">
                                    {{ item.statusText }}
                                </HistoryItemStatus>
                            </HistoryItemHeader>

                            <HistoryItemContent>
                                <HistoryItemInfo v-for="(info, infoIndex) in item.details" :key="infoIndex">
                                    <HistoryItemLabel>{{ info.label }}</HistoryItemLabel>
                                    <HistoryItemValue>{{ info.value }}</HistoryItemValue>
                                </HistoryItemInfo>
                            </HistoryItemContent>
                        </HistoryItem>
                    </HistoryList>
                    <HistoryEmpty v-else>
                        아직 발행된 현금영수증이 없습니다.
                    </HistoryEmpty>
                </HistoryCard>
            </HistoryGrid>
        </HistoryContent>
    </HistorySection>

    <!-- * 모달 바디 -->
    <MainContainer>
        <!-- * 왼쪽: 전자세금계산서 -->
        <LeftPanel>
            <DocumentSection>
                <DocumentHeader>
                    <DocumentTitle>
                        <DocumentIcon>
                            <FileOutline :size="24" />
                        </DocumentIcon>
                        전자세금계산서
                    </DocumentTitle>
                    <DocumentActions>
                        <PrimaryButton @click="issueTaxInvoice"
                            :disabled="isProcessing || taxInvoiceStatus === 'issued'"
                            :loading="isProcessing && activeDocument === 'taxInvoice'">
                            {{ getTaxInvoiceButtonText() }}
                        </PrimaryButton>
                    </DocumentActions>
                </DocumentHeader>

                <DocumentContent v-if="taxInvoiceStatus !== 'issued'">
                    <ConfigGrid>
                        <ConfigItem>
                            <ConfigLabel>작성일자</ConfigLabel>
                            <ConfigInput type="date" v-model="taxInvoiceConfig.writeDate" />
                        </ConfigItem>

                        <ConfigItem>
                            <ConfigLabel>과세형태</ConfigLabel>
                            <ConfigSelect v-model="taxInvoiceConfig.taxType">
                                <option value="과세">과세</option>
                                <option value="영세">영세</option>
                                <option value="면세">면세</option>
                            </ConfigSelect>
                        </ConfigItem>

                        <ConfigItem>
                            <ConfigLabel>공급받는자 유형</ConfigLabel>
                            <ConfigSelect v-model="taxInvoiceConfig.invoiceeType">
                                <option value="사업자">사업자</option>
                                <option value="개인">개인</option>
                                <option value="외국인">외국인</option>
                            </ConfigSelect>
                        </ConfigItem>

                        <ConfigItem>
                            <ConfigLabel>{{ getInvoiceeLabel() }}</ConfigLabel>
                            <ConfigInput v-model="taxInvoiceConfig.invoiceeCorpNum"
                                :placeholder="getInvoiceePlaceholder()"
                                :disabled="taxInvoiceConfig.invoiceeType === '외국인'" />
                            <ConfigHelper v-if="taxInvoiceConfig.invoiceeType === '외국인'">
                                외국인의 경우 자동으로 9999999999999로 설정
                            </ConfigHelper>
                        </ConfigItem>

                        <ConfigItem>
                            <ConfigLabel>공급받는자 상호</ConfigLabel>
                            <ConfigInput v-model="taxInvoiceConfig.invoiceeCorpName"
                                :value=taxInvoiceConfig.invoiceeCorpName />
                        </ConfigItem>

                        <ConfigItem>
                            <ConfigLabel>공급받는자 대표자 성명</ConfigLabel>
                            <ConfigInput v-model="taxInvoiceConfig.invoiceeCEOName"
                                :value=taxInvoiceConfig.invoiceeCEOName />
                        </ConfigItem>

                        <ConfigItem>
                            <ConfigLabel>주소</ConfigLabel>
                            <ConfigInput v-model="taxInvoiceConfig.invoicerAddr" :value=taxInvoiceConfig.invoicerAddr />
                        </ConfigItem>

                        <ConfigItem>
                            <ConfigLabel>업태</ConfigLabel>
                            <ConfigInput v-model="taxInvoiceConfig.invoicerBizType"
                                :value=taxInvoiceConfig.invoicerBizType />
                        </ConfigItem>

                        <ConfigItem>
                            <ConfigLabel>종목</ConfigLabel>
                            <ConfigInput v-model="taxInvoiceConfig.invoicerBizClass"
                                :value=taxInvoiceConfig.invoicerBizClass />
                        </ConfigItem>

                        <ConfigItem>
                            <ConfigLabel>이메일</ConfigLabel>
                            <ConfigInput v-model="taxInvoiceConfig.invoicerEmail"
                                :value=taxInvoiceConfig.invoicerEmail />
                        </ConfigItem>

                        <ConfigItem>
                            <ConfigLabel>합계금액</ConfigLabel>
                            <ConfigInput :value="formatNumber(taxInvoiceConfig.totalAmount)" placeholder="자동 계산"
                                @input="handleTotalAmountInput($event)" />
                        </ConfigItem>

                        <ConfigItem>
                            <ConfigLabel>공급가액 합계</ConfigLabel>
                            <ConfigInput :value="formatNumber(taxInvoiceConfig.supplyCostTotal)" placeholder="자동 계산"
                                @input="handleSupplyCostTotalInput($event)" />
                        </ConfigItem>

                        <ConfigItem>
                            <ConfigLabel>세액 합계</ConfigLabel>
                            <ConfigInput :value="formatNumber(taxInvoiceConfig.taxTotal)" placeholder="자동 계산"
                                @input="handleTaxTotalInput($event)" />
                        </ConfigItem>

                        <!-- * 품목 정보 섹션 -->
                        <ProductDetailSection v-for="(product, index) in taxInvoiceConfig.productDetails" :key="index">
                            <ProductDetailHeader>
                                <ProductDetailTitle>
                                    품목 {{ index + 1 }}
                                </ProductDetailTitle>
                                <ProductDetailRemoveButton v-if="taxInvoiceConfig.productDetails.length > 1"
                                    @click="removeProductDetail(index)">
                                    삭제
                                </ProductDetailRemoveButton>
                            </ProductDetailHeader>

                            <ProductDetailGrid>
                                <ProductDetailField>
                                    <ConfigLabel>일련번호</ConfigLabel>
                                    <ConfigInput v-model="product.serialNum" type="number" readonly />
                                </ProductDetailField>
                                <ProductDetailField>
                                    <ConfigLabel>거래일자</ConfigLabel>
                                    <ConfigInput v-model="product.purchaseDT" type="date" />
                                </ProductDetailField>
                                <ProductDetailField>
                                    <ConfigLabel>품명</ConfigLabel>
                                    <ConfigInput v-model="product.itemName" />
                                </ProductDetailField>
                                <ProductDetailField>
                                    <ConfigLabel>수량</ConfigLabel>
                                    <ConfigInput type="number" v-model="product.qty" min="1"
                                        @input="calculateProductAmounts(product)" />
                                </ProductDetailField>
                                <ProductDetailField>
                                    <ConfigLabel>단가</ConfigLabel>
                                    <ConfigInput :value="formatNumber(product.unitCost)" type="text"
                                        @input="handleUnitCostInput(product, $event)" />
                                </ProductDetailField>
                                <ProductDetailField>
                                    <ConfigLabel>공급가액</ConfigLabel>
                                    <ConfigInput :value="formatNumber(product.supplyCost)" type="text" readonly />
                                </ProductDetailField>
                                <ProductDetailField>
                                    <ConfigLabel>세액</ConfigLabel>
                                    <ConfigInput :value="formatNumber(product.tax)" type="text" readonly />
                                </ProductDetailField>
                            </ProductDetailGrid>
                        </ProductDetailSection>

                        <ProductAddButtonContainer>
                            <ProductAddButton @click="addProductDetail">
                                <span>+</span>
                                품목 추가
                            </ProductAddButton>
                        </ProductAddButtonContainer>
                    </ConfigGrid>
                </DocumentContent>
            </DocumentSection>
        </LeftPanel>

        <!-- * 오른쪽: 현금영수증 -->
        <RightPanel>
            <DocumentSection>
                <DocumentHeader>
                    <DocumentTitle>
                        <DocumentIcon>
                            <ReceiptTextOutline :size="24" />
                        </DocumentIcon>
                        현금영수증
                    </DocumentTitle>
                    <DocumentActions>
                        <PrimaryButton @click="issueCashReceipt"
                            :disabled="isProcessing || cashReceiptStatus === 'issued'"
                            :loading="isProcessing && activeDocument === 'cashReceipt'">
                            {{ getCashReceiptButtonText() }}
                        </PrimaryButton>
                    </DocumentActions>
                </DocumentHeader>

                <DocumentContent v-if="cashReceiptStatus !== 'issued'">
                    <ConfigGrid>
                        <ConfigItem>
                            <ConfigLabel>문서 형태</ConfigLabel>
                            <ConfigSelect v-model="cashReceiptConfig.tradeType">
                                <option value="승인거래">승인거래</option>
                                <option value="취소거래">취소거래</option>
                            </ConfigSelect>
                        </ConfigItem>

                        <ConfigItem>
                            <ConfigLabel>거래구분</ConfigLabel>
                            <ConfigSelect v-model="cashReceiptConfig.tradeUsage">
                                <option value="소득공제용">소득공제용</option>
                                <option value="지출증빙용">지출증빙용</option>
                            </ConfigSelect>
                        </ConfigItem>

                        <ConfigItem>
                            <ConfigLabel>과세형태</ConfigLabel>
                            <ConfigSelect v-model="cashReceiptConfig.taxationType">
                                <option value="과세">과세</option>
                                <option value="비과세">비과세</option>
                            </ConfigSelect>
                        </ConfigItem>

                        <ConfigItem>
                            <ConfigLabel>거래금액</ConfigLabel>
                            <ConfigInput
                                :value="formatNumber(cashReceiptConfig.totalAmount || calculatedCashReceiptAmounts.totalAmount || 0)"
                                placeholder="자동 계산" @input="handleCashReceiptTotalAmountInput($event)" />
                        </ConfigItem>

                        <ConfigItem>
                            <ConfigLabel>공급가액</ConfigLabel>
                            <ConfigInput
                                :value="formatNumber(cashReceiptConfig.supplyCost || calculatedCashReceiptAmounts.supplyCostTotal || 0)"
                                placeholder="자동 계산" @input="handleCashReceiptSupplyCostInput($event)" />
                        </ConfigItem>

                        <ConfigItem>
                            <ConfigLabel>부가세</ConfigLabel>
                            <ConfigInput
                                :value="formatNumber(cashReceiptConfig.tax || calculatedCashReceiptAmounts.taxTotal || 0)"
                                placeholder="자동 계산" @input="handleCashReceiptTaxInput($event)" />
                        </ConfigItem>

                        <ConfigItem>
                            <ConfigLabel>식별번호</ConfigLabel>
                            <ConfigInput v-model="cashReceiptConfig.identityNum" />
                        </ConfigItem>
                    </ConfigGrid>
                </DocumentContent>
            </DocumentSection>
        </RightPanel>
    </MainContainer>
</template>

<script>
import * as DocumentIssuanceModalCss from '@/assets/styles/order/modal/DocumentIssuanceModalCss.js';

import FileOutline from 'vue-material-design-icons/FileOutline.vue';
import ReceiptTextOutline from 'vue-material-design-icons/ReceiptTextOutline.vue';
import ChevronDown from 'vue-material-design-icons/ChevronDown.vue';
import Monitor from 'vue-material-design-icons/Monitor.vue';

export default {
    name: 'DocumentIssuanceModal',

    components: {
        ...DocumentIssuanceModalCss,
        FileOutline,
        ReceiptTextOutline,
        ChevronDown,
        Monitor
    },

    emits: ['close', 'document-issued', 'complete'],

    props: {
        orderId: {
            type: Number,
            required: true
        },
        orderData: {
            type: Object,
            default: () => ({})
        },
        claimType: {
            type: String,
            required: true,
            validator: value => ['oxygen', 'diabetes'].includes(value)
        },
        products: {
            type: Array,
            default: () => []
        }
    },

    mounted() {
        // 1. 품목 정보 먼저 초기화
        this.initializeProductDetails();
        // 2. orderData 기반 정보 초기화 (품목 금액 업데이트 포함)
        this.initializeOrderData();
        // 3. 기존 발행 내역 로드
        this.loadExistingDocumentHistory();
    },

    data() {
        return {
            taxInvoiceStatus: 'pending',
            cashReceiptStatus: 'pending',
            errorMessage: '',
            isProcessing: false,
            activeDocument: null,
            isHistoryExpanded: false,
            popbillCorpNum: this.$globalState.company_bnum,
            popbillUserId: this.$globalState.popbill_id,
            taxInvoiceHistory: [],
            cashReceiptHistory: [],

            taxInvoiceConfig: {
                issueType: '정발행',
                taxType: '과세',
                chargeDirection: '정과금',
                writeDate: this.formatDateForInput(new Date()),
                purposeType: '영수',
                supplyCostTotal: 0,
                taxTotal: 0,
                totalAmount: 0,
                invoicerCorpNum: this.$globalState.company_bnum.toString(),
                invoicerCorpName: this.$globalState.company,
                invoicerCEOName: this.$globalState.company_ceo_name,
                invoicerAddr: this.$globalState.company_address,
                invoicerBizType: this.$globalState.company_industry,
                invoicerBizClass: this.$globalState.company_type,
                invoicerEmail: this.$globalState.company_email,
                invoiceeType: '개인',
                invoiceeCorpNum: '',
                invoiceeCorpName: '',
                invoiceeCEOName: '',
                productDetails: []
            },

            cashReceiptConfig: {
                tradeType: '승인거래',
                tradeUsage: '소득공제용',
                taxationType: '과세',
                totalAmount: 0,
                supplyCost: 0,
                tax: 0,
                serviceFee: 0,
                identityNum: '',
            },
        }
    },

    computed: {
        today() {
            return this.formatDateForInput(new Date());
        },

        calculatedAmounts() {
            let totalAmount = 0;

            this.products.forEach((product) => {
                if (product.name1 && product.name1.includes('산소발생기')) {
                    const productPrice = product.rentprice || 0;
                    const quantity = product.quantity || 1;
                    const totalProductPrice = productPrice * quantity;
                    const discountedPrice = Math.round(totalProductPrice * 0.9);
                    totalAmount += discountedPrice;
                } else {
                    const amount = (product.unitprice || 0) * (product.quantity || 1);
                    totalAmount += amount;
                }
            });

            const supplyCostTotal = Math.round(totalAmount / 1.1);
            const taxTotal = totalAmount - supplyCostTotal;

            return {
                supplyCostTotal,
                taxTotal,
                totalAmount
            };
        },

        calculatedCashReceiptAmounts() {
            let supplyCostTotal = 0;
            let taxTotal = 0;
            let totalAmount = 0;

            this.products.forEach((product) => {
                if (product.name1 && product.name1.includes('산소발생기')) {
                    const productPrice = product.rentprice || 0;
                    const quantity = product.quantity || 1;
                    const totalProductPrice = productPrice * quantity;
                    const copaymentAmount = Math.round(totalProductPrice * 0.1);
                    const supplyCost = Math.round(copaymentAmount / 1.1);
                    const tax = copaymentAmount - supplyCost;
                    supplyCostTotal += supplyCost;
                    taxTotal += tax;
                    totalAmount += copaymentAmount;
                } else {
                    const amount = (product.unitprice || 0) * (product.quantity || 1);
                    const copaymentAmount = Math.round(amount * 0.1);
                    const supplyCost = Math.round(copaymentAmount / 1.1);
                    const tax = copaymentAmount - supplyCost;
                    supplyCostTotal += supplyCost;
                    taxTotal += tax;
                    totalAmount += copaymentAmount;
                }
            });

            return {
                supplyCostTotal,
                taxTotal,
                totalAmount
            };
        },
    },

    methods: {
        // 전자세금계산서 - 합계금액 입력 처리
        handleTotalAmountInput(event) {
            const value = event.target.value.replace(/,/g, '');
            const numericValue = parseFloat(value) || 0;
            this.taxInvoiceConfig.totalAmount = numericValue;

            // 공급가액과 세액 재계산
            const supplyCostTotal = Math.round(numericValue / 1.1);
            const taxTotal = numericValue - supplyCostTotal;
            this.taxInvoiceConfig.supplyCostTotal = supplyCostTotal;
            this.taxInvoiceConfig.taxTotal = taxTotal;
        },

        // 전자세금계산서 - 공급가액 합계 입력 처리
        handleSupplyCostTotalInput(event) {
            const value = event.target.value.replace(/,/g, '');
            const numericValue = parseFloat(value) || 0;
            this.taxInvoiceConfig.supplyCostTotal = numericValue;

            // 합계금액과 세액 재계산
            const taxTotal = this.taxInvoiceConfig.taxTotal || 0;
            this.taxInvoiceConfig.totalAmount = numericValue + taxTotal;
        },

        // 전자세금계산서 - 세액 합계 입력 처리
        handleTaxTotalInput(event) {
            const value = event.target.value.replace(/,/g, '');
            const numericValue = parseFloat(value) || 0;
            this.taxInvoiceConfig.taxTotal = numericValue;

            // 합계금액 재계산
            const supplyCostTotal = this.taxInvoiceConfig.supplyCostTotal || 0;
            this.taxInvoiceConfig.totalAmount = supplyCostTotal + numericValue;
        },

        // 현금영수증 - 거래금액 입력 처리
        handleCashReceiptTotalAmountInput(event) {
            const value = event.target.value.replace(/,/g, '');
            const numericValue = parseFloat(value) || 0;
            this.cashReceiptConfig.totalAmount = numericValue;

            // 공급가액과 부가세 재계산
            const supplyCost = Math.round(numericValue / 1.1);
            const tax = numericValue - supplyCost;
            this.cashReceiptConfig.supplyCost = supplyCost;
            this.cashReceiptConfig.tax = tax;
        },

        // 현금영수증 - 공급가액 입력 처리
        handleCashReceiptSupplyCostInput(event) {
            const value = event.target.value.replace(/,/g, '');
            const numericValue = parseFloat(value) || 0;
            this.cashReceiptConfig.supplyCost = numericValue;

            // 거래금액과 부가세 재계산
            const tax = this.cashReceiptConfig.tax || 0;
            this.cashReceiptConfig.totalAmount = numericValue + tax;
        },

        // 현금영수증 - 부가세 입력 처리
        handleCashReceiptTaxInput(event) {
            const value = event.target.value.replace(/,/g, '');
            const numericValue = parseFloat(value) || 0;
            this.cashReceiptConfig.tax = numericValue;

            // 거래금액 재계산
            const supplyCost = this.cashReceiptConfig.supplyCost || 0;
            this.cashReceiptConfig.totalAmount = supplyCost + numericValue;
        },

        handleUnitCostInput(product, event) {
            // 천단위 구분자 제거
            const value = event.target.value.replace(/,/g, '');
            const numericValue = parseFloat(value) || 0;

            // product.unitCost에 숫자 값 저장
            product.unitCost = numericValue;

            // 금액 계산
            this.calculateProductAmounts(product);
        },

        // 품목별 금액 계산 (단가, 수량 변경 시 호출)
        calculateProductAmounts(product) {
            const quantity = parseInt(product.qty) || 1;
            const unitPrice = parseFloat(product.unitCost) || 0;
            const totalPrice = quantity * unitPrice;
            const supplyCost = Math.round(totalPrice / 1.1);
            const tax = totalPrice - supplyCost;

            product.supplyCost = supplyCost;
            product.tax = tax;

            this.updateCalculatedAmounts();
        },

        // 품목 상세 정보 초기화
        initializeProductDetails() {
            // products가 없거나 비어있으면 기본 품목 추가
            if (!this.products || this.products.length === 0) {
                // orderData에 세금계산서 금액이 있으면 기본 품목 추가
                if (this.orderData?.taxInvoiceAmount) {
                    const totalAmount = this.parseAmount(this.orderData.taxInvoiceAmount);
                    const supplyCost = Math.round(totalAmount / 1.1);
                    const tax = totalAmount - supplyCost;

                    // 거래일자 설정: 종료일이 있으면 사용, 없으면 작성일자 사용
                    let purchaseDate = this.taxInvoiceConfig.writeDate;
                    if (this.orderData?.useHomeEndDate) {
                        purchaseDate = this.formatDateForInput(new Date(this.orderData.useHomeEndDate));
                    } else if (this.orderData?.useMobileEndDate) {
                        purchaseDate = this.formatDateForInput(new Date(this.orderData.useMobileEndDate));
                    } else if (this.orderData?.prescriptionData?.useHomeEndDate) {
                        purchaseDate = this.formatDateForInput(new Date(this.orderData.prescriptionData.useHomeEndDate));
                    } else if (this.orderData?.prescriptionData?.useMobileEndDate) {
                        purchaseDate = this.formatDateForInput(new Date(this.orderData.prescriptionData.useMobileEndDate));
                    } else if (this.orderData.taxInvoiceDate) {
                        const taxInvoiceDate = this.orderData.taxInvoiceDate instanceof Date
                            ? this.orderData.taxInvoiceDate
                            : new Date(this.orderData.taxInvoiceDate);
                        purchaseDate = this.formatDateForInput(taxInvoiceDate);
                    }

                    this.taxInvoiceConfig.productDetails = [{
                        serialNum: 1,
                        purchaseDT: purchaseDate,
                        itemName: '산소치료',
                        qty: 1,
                        unitPrice: totalAmount,
                        unitCost: totalAmount,
                        supplyCost: supplyCost,
                        tax: tax,
                        productId: null,
                        originalProduct: null
                    }];
                } else {
                    this.taxInvoiceConfig.productDetails = [];
                }
                return;
            }

            // products가 있으면 기존 로직대로 처리
            this.taxInvoiceConfig.productDetails = this.products.map((product, index) => {
                // 품명 생성 (가정용/휴대용 구분)
                let itemName = '';
                let isHomeType = false;
                let isMobileType = false;

                if (product.name1 && product.name1.includes('산소발생기')) {
                    if (product.name2 === '휴대용') {
                        itemName = '휴대용 산소발생기 임대료';
                        isMobileType = true;
                    } else if (product.name2 === '가정용' || product.name3 === '산소발생기가정용') {
                        itemName = '가정용 산소발생기 임대료';
                        isHomeType = true;
                    } else {
                        itemName = '산소발생기 임대료';
                    }
                } else {
                    // 품명 생성: name1 우선, 없으면 name3, 없으면 name
                    itemName = product.name1 || product.name3 || product.name || '상품명';
                }

                const quantity = product.quantity || 1;
                const unitPrice = product.rentprice || product.unitprice || 0;
                const totalPrice = quantity * unitPrice;

                let finalAmount = totalPrice;

                if (product.name1 && product.name1.includes('산소발생기')) {
                    finalAmount = Math.round(totalPrice * 0.9);
                }

                const supplyCost = Math.round(finalAmount / 1.1);
                const tax = finalAmount - supplyCost;

                // 거래일자 설정: 품목 타입에 따라 종료일 사용
                let purchaseDate = this.formatDateForInput(new Date(this.taxInvoiceConfig.writeDate));

                if (isHomeType) {
                    // 가정용인 경우 가정용 사용기간 종료일 사용
                    if (this.orderData?.useHomeEndDate) {
                        purchaseDate = this.formatDateForInput(new Date(this.orderData.useHomeEndDate));
                    } else if (this.orderData?.prescriptionData?.useHomeEndDate) {
                        purchaseDate = this.formatDateForInput(new Date(this.orderData.prescriptionData.useHomeEndDate));
                    } else if (this.orderData?.taxInvoiceDate) {
                        const taxInvoiceDate = this.orderData.taxInvoiceDate instanceof Date
                            ? this.orderData.taxInvoiceDate
                            : new Date(this.orderData.taxInvoiceDate);
                        purchaseDate = this.formatDateForInput(taxInvoiceDate);
                    }
                } else if (isMobileType) {
                    // 휴대용인 경우 휴대용 사용기간 종료일 사용
                    if (this.orderData?.useMobileEndDate) {
                        purchaseDate = this.formatDateForInput(new Date(this.orderData.useMobileEndDate));
                    } else if (this.orderData?.prescriptionData?.useMobileEndDate) {
                        purchaseDate = this.formatDateForInput(new Date(this.orderData.prescriptionData.useMobileEndDate));
                    } else if (this.orderData?.taxInvoiceDate) {
                        const taxInvoiceDate = this.orderData.taxInvoiceDate instanceof Date
                            ? this.orderData.taxInvoiceDate
                            : new Date(this.orderData.taxInvoiceDate);
                        purchaseDate = this.formatDateForInput(taxInvoiceDate);
                    }
                } else {
                    // 타입이 명확하지 않은 경우 세금계산서 작성일자 또는 orderData의 날짜 사용
                    if (this.orderData?.taxInvoiceDate) {
                        const taxInvoiceDate = this.orderData.taxInvoiceDate instanceof Date
                            ? this.orderData.taxInvoiceDate
                            : new Date(this.orderData.taxInvoiceDate);
                        purchaseDate = this.formatDateForInput(taxInvoiceDate);
                    }
                }

                return {
                    serialNum: index + 1,
                    purchaseDT: purchaseDate,
                    itemName: itemName,
                    qty: quantity,
                    unitPrice: unitPrice,
                    unitCost: unitPrice,
                    supplyCost: supplyCost,
                    tax: tax,
                    productId: product.id,
                    originalProduct: product
                };
            });

            // 초기화 후 전체 금액 계산
            this.updateCalculatedAmounts();
        },

        // 품목 추가
        addProductDetail() {
            const newIndex = this.taxInvoiceConfig.productDetails.length;
            this.taxInvoiceConfig.productDetails.push({
                serialNum: newIndex + 1,
                purchaseDT: this.formatDateForInput(new Date(this.taxInvoiceConfig.writeDate)),
                itemName: '',
                qty: 1,
                unitPrice: 0,
                unitCost: 0,
                supplyCost: 0,
                tax: 0,
                productId: null,
                originalProduct: null
            });
        },

        // 품목 삭제
        removeProductDetail(index) {
            if (this.taxInvoiceConfig.productDetails.length > 1) {
                this.taxInvoiceConfig.productDetails.splice(index, 1);
                // 삭제 후 전체 금액 재계산
                this.updateCalculatedAmounts();
            }
        },

        // 기존 발행 내역 로드
        async loadExistingDocumentHistory() {
            try {
                const response = await this.$axios.get(`${this.$apiUrls.hanqmedi}/checkDocumentIssuance`, {
                    params: {
                        orderId: this.orderId,
                        claimType: this.claimType
                    }
                });

                if (response.data && response.data.success) {
                    const {
                        hasTaxInvoice,
                        hasCashReceipt,
                        taxInvoice,
                        cashReceipt,
                        taxInvoiceDate,
                        taxInvoiceAmount,
                        cashReceiptDate,
                        cashReceiptAmount,
                        taxInvoiceMgtKey,
                        cashReceiptMgtKey
                    } = response.data;

                    if (hasTaxInvoice && taxInvoice) {
                        this.taxInvoiceStatus = 'issued';

                        // 문서번호가 있으면 팝빌 API로 상세정보 조회
                        if (taxInvoiceMgtKey) {
                            try {
                                const detailResponse = await this.$axios.get(`${this.$apiUrls.hanqmedi}/popbill/taxinvoice/getDetailInfo`, {
                                    params: {
                                        mgtKey: taxInvoiceMgtKey,
                                        CorpNum: this.taxInvoiceConfig.invoicerCorpNum,
                                        UserID: this.$globalState.popbill_id
                                    }
                                });

                                if (detailResponse.data && detailResponse.data.code === 1) {
                                    const detailData = detailResponse.data.data || detailResponse.data;
                                    this.updateTaxInvoiceHistory(detailData);
                                } else {
                                    // 팝빌 조회 실패 시 DB 데이터 사용
                                    this.updateTaxInvoiceHistory({
                                        ntsconfirmNum: taxInvoice,
                                        writeDate: taxInvoiceDate,
                                        totalAmount: taxInvoiceAmount,
                                        mgtKey: taxInvoiceMgtKey,
                                        isFromDB: true
                                    });
                                }
                            } catch (error) {
                                console.error('팝빌 상세정보 조회 실패:', error);
                                // 팝빌 조회 실패 시 DB 데이터 사용
                                this.updateTaxInvoiceHistory({
                                    ntsconfirmNum: taxInvoice,
                                    writeDate: taxInvoiceDate,
                                    totalAmount: taxInvoiceAmount,
                                    mgtKey: taxInvoiceMgtKey,
                                    isFromDB: true
                                });
                            }
                        } else {
                            // 문서번호가 없으면 DB 데이터만 사용
                            this.updateTaxInvoiceHistory({
                                ntsconfirmNum: taxInvoice,
                                writeDate: taxInvoiceDate,
                                totalAmount: taxInvoiceAmount,
                                isFromDB: true
                            });
                        }
                    }

                    if (hasCashReceipt && cashReceipt) {
                        this.cashReceiptStatus = 'issued';

                        // 문서번호가 있으면 팝빌 API로 상세정보 조회
                        if (cashReceiptMgtKey) {
                            try {
                                const detailResponse = await this.$axios.get(`${this.$apiUrls.hanqmedi}/popbill/cashbill/getDetailInfo`, {
                                    params: {
                                        mgtKey: cashReceiptMgtKey,
                                        CorpNum: this.taxInvoiceConfig.invoicerCorpNum,
                                        UserID: this.$globalState.popbill_id
                                    }
                                });

                                if (detailResponse.data && detailResponse.data.mgtKey) {
                                    this.updateCashReceiptHistory(detailResponse.data);
                                } else {
                                    // 팝빌 조회 실패 시 DB 데이터 사용
                                    this.updateCashReceiptHistory({
                                        confirmNum: cashReceipt,
                                        tradeDT: cashReceiptDate,
                                        totalAmount: cashReceiptAmount,
                                        mgtKey: cashReceiptMgtKey,
                                        isFromDB: true
                                    });
                                }
                            } catch (error) {
                                console.error('팝빌 상세정보 조회 실패:', error);
                                // 팝빌 조회 실패 시 DB 데이터 사용
                                this.updateCashReceiptHistory({
                                    confirmNum: cashReceipt,
                                    tradeDT: cashReceiptDate,
                                    totalAmount: cashReceiptAmount,
                                    mgtKey: cashReceiptMgtKey,
                                    isFromDB: true
                                });
                            }
                        } else {
                            // 문서번호가 없으면 DB 데이터만 사용
                            this.updateCashReceiptHistory({
                                confirmNum: cashReceipt,
                                tradeDT: cashReceiptDate,
                                totalAmount: cashReceiptAmount,
                                isFromDB: true
                            });
                        }
                    }
                }
            } catch (error) {
                // 404 에러는 팝빌에서 문서를 찾지 못한 것이므로 정상 처리
                if (error.response?.status === 404) {
                    console.warn('팝빌 상세정보 조회 실패 (404): 문서를 찾을 수 없습니다. DB 데이터를 사용합니다.', taxInvoiceMgtKey);
                } else {
                    console.error('팝빌 상세정보 조회 실패:', error);
                }
                // 팝빌 조회 실패 시 DB 데이터 사용
                this.updateTaxInvoiceHistory({
                    ntsconfirmNum: taxInvoice,
                    writeDate: taxInvoiceDate,
                    totalAmount: taxInvoiceAmount,
                    mgtKey: taxInvoiceMgtKey,
                    isFromDB: true
                });
            }
        },

        // 전자세금계산서 발행 내역 업데이트 (통합)
        async updateTaxInvoiceHistory(responseData = null) {
            if (this.taxInvoiceStatus !== 'issued') return;

            let details = [];

            // responseData가 null이면 DB에서 정보 조회
            if (!responseData) {
                try {
                    const response = await this.$axios.get(`${this.$apiUrls.hanqmedi}/checkDocumentIssuance`, {
                        params: {
                            orderId: this.orderId,
                            claimType: this.claimType
                        }
                    });

                    if (response.data && response.data.success && response.data.hasTaxInvoice && response.data.taxInvoice) {
                        // DB에서 문서번호 가져오기
                        const mgtKey = response.data.taxInvoiceMgtKey;

                        // 문서번호가 있으면 팝빌 API로 상세정보 조회
                        if (mgtKey) {
                            try {
                                const detailResponse = await this.$axios.get(`${this.$apiUrls.hanqmedi}/popbill/taxinvoice/getDetailInfo`, {
                                    params: {
                                        mgtKey: mgtKey,
                                        CorpNum: this.taxInvoiceConfig.invoicerCorpNum,
                                        UserID: this.$globalState.popbill_id
                                    }
                                });

                                if (detailResponse.data && detailResponse.data.code === 1) {
                                    const detailData = detailResponse.data.data || detailResponse.data;
                                    // 팝빌 응답 데이터 사용
                                    responseData = detailData;
                                    responseData.mgtKey = mgtKey; // 문서번호 확실히 설정
                                } else {
                                    // 팝빌 조회 실패 시 DB 데이터만 사용
                                    responseData = {
                                        ntsconfirmNum: response.data.taxInvoice,
                                        writeDate: response.data.taxInvoiceDate,
                                        totalAmount: response.data.taxInvoiceAmount,
                                        mgtKey: mgtKey,
                                        isFromDB: true
                                    };
                                }
                            } catch (error) {
                                console.error('팝빌 상세정보 조회 실패:', error);
                                // 팝빌 조회 실패 시 DB 데이터만 사용
                                responseData = {
                                    ntsconfirmNum: response.data.taxInvoice,
                                    writeDate: response.data.taxInvoiceDate,
                                    totalAmount: response.data.taxInvoiceAmount,
                                    mgtKey: mgtKey,
                                    isFromDB: true
                                };
                            }
                        } else {
                            // 문서번호가 없으면 DB 데이터만 사용
                            responseData = {
                                ntsconfirmNum: response.data.taxInvoice,
                                writeDate: response.data.taxInvoiceDate,
                                totalAmount: response.data.taxInvoiceAmount,
                                isFromDB: true
                            };
                        }
                    }
                } catch (error) {
                    console.error('DB에서 발행 정보 조회 실패:', error);
                }
            }

            if (responseData && responseData.isFromDB) {
                // DB에서 가져온 정보
                const totalAmount = parseInt(responseData.totalAmount || 0);
                const supplyCostTotal = Math.round(totalAmount / 1.1);
                const taxTotal = totalAmount - supplyCostTotal;

                // 문서번호: DB에서 가져온 것이 있으면 사용, 없으면 생성
                const documentNumber = responseData.mgtKey || `TI${Date.now()}`;

                details = [
                    { label: '발행일시', value: responseData.writeDate },
                    { label: '국세청 승인번호', value: responseData.ntsconfirmNum || '' },
                    { label: '문서번호', value: documentNumber },
                    { label: '발행유형', value: this.taxInvoiceConfig.issueType },
                    { label: '과세형태', value: this.taxInvoiceConfig.taxType },
                    { label: '공급자 상호', value: this.taxInvoiceConfig.invoicerCorpName },
                    { label: '공급자 등록번호', value: this.taxInvoiceConfig.invoicerCorpNum },
                    { label: '공급받는자 유형', value: this.taxInvoiceConfig.invoiceeType },
                    { label: '공급받는자 상호', value: this.taxInvoiceConfig.invoiceeCorpName },
                    { label: '공급받는자 등록번호', value: this.taxInvoiceConfig.invoiceeCorpNum },
                    { label: '공급가액 합계', value: this.formatNumber(supplyCostTotal) + '원' },
                    { label: '세액 합계', value: this.formatNumber(taxTotal) + '원' },
                    { label: '합계금액', value: this.formatNumber(totalAmount) + '원' },
                ];

                if (this.taxInvoiceConfig.productDetails && this.taxInvoiceConfig.productDetails.length > 0) {
                    this.taxInvoiceConfig.productDetails.forEach((product, index) => {
                        details.push({
                            label: `품목 ${index + 1}`,
                            value: `${product.itemName} (수량: ${product.qty}, 단가: ${this.formatNumber(product.unitCost)}원, 공급가액: ${this.formatNumber(product.supplyCost)}원, 세액: ${this.formatNumber(product.tax)}원)`
                        });
                    });
                }
            } else if (responseData) {
                // 팝빌 API 응답 데이터
                details = [
                    { label: '발행일시', value: responseData.writeDate },
                    { label: '국세청 승인번호', value: responseData.ntsconfirmNum || '' },
                    { label: '문서번호', value: responseData.mgtKey || `TI${Date.now()}` },
                    { label: '발행유형', value: responseData.issueType || this.taxInvoiceConfig.issueType },
                    { label: '과세형태', value: responseData.taxType || this.taxInvoiceConfig.taxType },
                    { label: '공급가액 합계', value: this.formatNumber(parseInt(responseData.supplyCostTotal || 0)) + '원' },
                    { label: '세액 합계', value: this.formatNumber(parseInt(responseData.taxTotal || 0)) + '원' },
                    { label: '합계금액', value: this.formatNumber(parseInt(responseData.totalAmount || 0)) + '원' },
                    { label: '공급자 사업자번호', value: responseData.invoicerCorpNum || this.taxInvoiceConfig.invoicerCorpNum },
                    { label: '공급자 상호', value: responseData.invoicerCorpName || this.taxInvoiceConfig.invoicerCorpName },
                    { label: '공급받는자 유형', value: responseData.invoiceeType || this.taxInvoiceConfig.invoiceeType },
                    { label: '공급받는자 상호', value: responseData.invoiceeCorpName || this.taxInvoiceConfig.invoiceeCorpName },
                    { label: '공급받는자 사업자번호', value: responseData.invoiceeCorpNum || this.taxInvoiceConfig.invoiceeCorpNum },
                ];

                if (responseData.issueStatus) {
                    details.push({ label: '발행상태', value: responseData.issueStatus });
                }

                if (responseData.detailList && responseData.detailList.length > 0) {
                    responseData.detailList.forEach((item, index) => {
                        details.push({
                            label: `품목 ${index + 1}`,
                            value: `${item.itemName} (수량: ${item.qty}, 단가: ${this.formatNumber(parseInt(item.unitCost))}원, 공급가액: ${this.formatNumber(parseInt(item.supplyCost))}원, 세액: ${this.formatNumber(parseInt(item.tax))}원)`
                        });
                    });
                } else if (this.taxInvoiceConfig.productDetails && this.taxInvoiceConfig.productDetails.length > 0) {
                    this.taxInvoiceConfig.productDetails.forEach((product, index) => {
                        details.push({
                            label: `품목 ${index + 1}`,
                            value: `${product.itemName} (수량: ${product.qty}, 단가: ${this.formatNumber(product.unitCost)}원, 공급가액: ${this.formatNumber(product.supplyCost)}원, 세액: ${this.formatNumber(product.tax)}원)`
                        });
                    });
                }
            } else {
                // DB에서도 정보를 찾지 못한 경우 기본 발행 내역
                details = [
                    { label: '발행일시', value: this.formatDateTime(new Date()) },
                    { label: '국세청 승인번호', value: '' },
                    { label: '문서번호', value: `TI${Date.now()}` },
                    { label: '발행유형', value: this.taxInvoiceConfig.issueType },
                    { label: '과세형태', value: this.taxInvoiceConfig.taxType },
                    { label: '공급가액 합계', value: this.formatNumber(this.taxInvoiceConfig.supplyCostTotal) + '원' },
                    { label: '세액 합계', value: this.formatNumber(this.taxInvoiceConfig.taxTotal) + '원' },
                    { label: '합계금액', value: this.formatNumber(this.taxInvoiceConfig.totalAmount) + '원' },
                    { label: '공급자 사업자번호', value: this.taxInvoiceConfig.invoicerCorpNum },
                    { label: '공급자 상호', value: this.taxInvoiceConfig.invoicerCorpName },
                    { label: '공급받는자 유형', value: this.taxInvoiceConfig.invoiceeType },
                    { label: '공급받는자 상호', value: this.taxInvoiceConfig.invoiceeCorpName },
                    { label: '공급받는자 사업자번호', value: this.taxInvoiceConfig.invoiceeCorpNum },
                ];

                if (this.taxInvoiceConfig.productDetails && this.taxInvoiceConfig.productDetails.length > 0) {
                    this.taxInvoiceConfig.productDetails.forEach((product, index) => {
                        details.push({
                            label: `품목 ${index + 1}`,
                            value: `${product.itemName} (수량: ${product.qty}, 단가: ${this.formatNumber(product.unitCost)}원, 공급가액: ${this.formatNumber(product.supplyCost)}원, 세액: ${this.formatNumber(product.tax)}원)`
                        });
                    });
                }
            }

            this.taxInvoiceHistory = [{
                title: '전자세금계산서 발행 완료',
                status: 'issued',
                statusText: '발행완료',
                details: details
            }];
        },

        // 현금영수증 발행 내역 업데이트 (통합)
        async updateCashReceiptHistory(responseData = null) {
            if (this.cashReceiptStatus !== 'issued') return;

            let details = [];

            // responseData가 null이면 DB에서 정보 조회
            if (!responseData) {
                try {
                    const response = await this.$axios.get(`${this.$apiUrls.hanqmedi}/checkDocumentIssuance`, {
                        params: {
                            orderId: this.orderId,
                            claimType: this.claimType
                        }
                    });

                    if (response.data && response.data.success && response.data.hasCashReceipt && response.data.cashReceipt) {
                        // DB에서 문서번호 가져오기
                        const mgtKey = response.data.cashReceiptMgtKey;

                        // 문서번호가 있으면 팝빌 API로 상세정보 조회
                        if (mgtKey) {
                            try {
                                const detailResponse = await this.$axios.get(`${this.$apiUrls.hanqmedi}/popbill/cashbill/getDetailInfo`, {
                                    params: {
                                        mgtKey: mgtKey,
                                        CorpNum: this.taxInvoiceConfig.invoicerCorpNum,
                                        UserID: this.$globalState.popbill_id
                                    }
                                });

                                if (detailResponse.data && detailResponse.data.code === 1) {
                                    const detailData = detailResponse.data.data || detailResponse.data;
                                    // 팝빌 응답 데이터 사용
                                    responseData = detailData;
                                    responseData.mgtKey = mgtKey; // 문서번호 확실히 설정
                                } else {
                                    // 팝빌 조회 실패 시 DB 데이터만 사용
                                    responseData = {
                                        ntsconfirmNum: response.data.taxInvoice,
                                        writeDate: response.data.taxInvoiceDate,
                                        totalAmount: response.data.taxInvoiceAmount,
                                        mgtKey: mgtKey,
                                        isFromDB: true
                                    };
                                }
                            } catch (error) {
                                console.error('팝빌 상세정보 조회 실패:', error);
                                // 팝빌 조회 실패 시 DB 데이터만 사용
                                responseData = {
                                    confirmNum: response.data.cashReceipt,
                                    tradeDT: response.data.cashReceiptDate,
                                    totalAmount: response.data.cashReceiptAmount,
                                    mgtKey: mgtKey,
                                    isFromDB: true
                                };
                            }
                        } else {
                            // 문서번호가 없으면 DB 데이터만 사용
                            responseData = {
                                confirmNum: response.data.cashReceipt,
                                tradeDT: response.data.cashReceiptDate,
                                totalAmount: response.data.cashReceiptAmount,
                                isFromDB: true
                            };
                        }
                    }
                } catch (error) {
                    if (error.response?.status === 404) {
                        console.warn('팝빌 상세정보 조회 실패 (404): 문서를 찾을 수 없습니다. DB 데이터를 사용합니다.', mgtKey);
                    } else {
                        console.error('팝빌 상세정보 조회 실패:', error);
                    }
                    // 팝빌 조회 실패 시 DB 데이터만 사용
                    responseData = {
                        ntsconfirmNum: response.data.taxInvoice,
                        writeDate: response.data.taxInvoiceDate,
                        totalAmount: response.data.taxInvoiceAmount,
                        mgtKey: mgtKey,
                        isFromDB: true
                    };
                }
            }

            if (responseData && responseData.isFromDB) {
                // DB에서 가져온 정보
                const totalAmount = parseInt(responseData.totalAmount || 0);
                const supplyCost = Math.round(totalAmount / 1.1);
                const tax = totalAmount - supplyCost;

                // 문서번호: DB에서 가져온 것이 있으면 사용, 없으면 생성
                const documentNumber = responseData.mgtKey || `CR${Date.now()}`;

                details = [
                    { label: '발행일시', value: responseData.tradeDT },
                    { label: '국세청 승인번호', value: responseData.confirmNum || '' },
                    { label: '문서번호', value: documentNumber },
                    { label: '문서형태', value: this.cashReceiptConfig.tradeType },
                    { label: '거래구분', value: this.cashReceiptConfig.tradeUsage },
                    { label: '거래유형', value: '일반' },
                    { label: '과세형태', value: this.cashReceiptConfig.taxationType },
                    { label: '가맹점 사업자번호', value: this.taxInvoiceConfig.invoicerCorpNum },
                    { label: '식별번호', value: this.cashReceiptConfig.identityNum },
                    { label: '거래금액', value: this.formatNumber(totalAmount) + '원' },
                    { label: '공급가액', value: this.formatNumber(supplyCost) + '원' },
                    { label: '부가세', value: this.formatNumber(tax) + '원' },
                ];
            } else if (responseData) {
                // 팝빌 API 응답 데이터
                details = [
                    { label: '발행일시', value: this.formatDateTimeFromString(responseData.tradeDT) || this.formatDateTime(new Date()) },
                    { label: '국세청 승인번호', value: responseData.confirmNum || '' },
                    { label: '문서번호', value: responseData.mgtKey || `CR${Date.now()}` },
                    { label: '문서형태', value: responseData.tradeType || this.cashReceiptConfig.tradeType },
                    { label: '거래구분', value: responseData.tradeUsage || this.cashReceiptConfig.tradeUsage },
                    { label: '거래유형', value: responseData.tradeOpt || '일반' },
                    { label: '과세형태', value: responseData.taxationType || this.cashReceiptConfig.taxationType },
                    { label: '거래금액', value: this.formatNumber(parseInt(responseData.totalAmount || 0)) + '원' },
                    { label: '공급가액', value: this.formatNumber(parseInt(responseData.supplyCost || 0)) + '원' },
                    { label: '부가세', value: this.formatNumber(parseInt(responseData.tax || 0)) + '원' },
                    { label: '가맹점 사업자번호', value: responseData.franchiseCorpNum || this.taxInvoiceConfig.invoicerCorpNum },
                    { label: '식별번호', value: responseData.identityNum || this.cashReceiptConfig.identityNum },
                ];

                console.log('details:', details);
            } else {
                // DB에서도 정보를 찾지 못한 경우 기본 발행 내역
                details = [
                    { label: '발행일시', value: this.formatDateTime(new Date()) },
                    { label: '국세청 승인번호', value: '' },
                    { label: '문서번호', value: `CR${Date.now()}` },
                    { label: '문서형태', value: this.cashReceiptConfig.tradeType },
                    { label: '거래구분', value: this.cashReceiptConfig.tradeUsage },
                    { label: '거래유형', value: '일반' },
                    { label: '과세형태', value: this.cashReceiptConfig.taxationType },
                    { label: '거래금액', value: this.formatNumber(this.calculatedCashReceiptAmounts.totalAmount) + '원' },
                    { label: '공급가액', value: this.formatNumber(this.calculatedCashReceiptAmounts.supplyCostTotal) + '원' },
                    { label: '부가세', value: this.formatNumber(this.calculatedCashReceiptAmounts.taxTotal) + '원' },
                    { label: '가맹점 사업자번호', value: this.taxInvoiceConfig.invoicerCorpNum },
                    { label: '식별번호', value: this.cashReceiptConfig.identityNum }
                ];
            }

            this.cashReceiptHistory = [{
                title: '현금영수증 발행 완료',
                status: 'issued',
                statusText: '발행완료',
                details: details
            }];
        },

        initializeOrderData() {
            if (!this.orderData) return;

            const name = this.orderData.name || '';
            const residentNumber = this.orderData.residentNumber || '';
            const residentNumberWithoutHyphen = residentNumber.replace(/-/g, '');

            this.taxInvoiceConfig.invoiceeType = '개인';
            this.taxInvoiceConfig.invoiceeCorpNum = residentNumberWithoutHyphen;
            this.taxInvoiceConfig.invoiceeCorpName = name;
            this.taxInvoiceConfig.invoiceeCEOName = name;

            if (this.orderData.taxInvoiceDate) {
                const taxInvoiceDate = this.orderData.taxInvoiceDate instanceof Date
                    ? this.orderData.taxInvoiceDate
                    : new Date(this.orderData.taxInvoiceDate);
                this.taxInvoiceConfig.writeDate = this.formatDateForInput(taxInvoiceDate);
            } else {
                this.taxInvoiceConfig.writeDate = this.formatDateForInput(new Date());
            }

            this.cashReceiptConfig.identityNum = residentNumberWithoutHyphen;

            // 세금계산서 금액 설정
            if (this.orderData.taxInvoiceAmount) {
                const totalAmount = this.parseAmount(this.orderData.taxInvoiceAmount);
                const supplyCostTotal = Math.round(totalAmount / 1.1);
                const taxTotal = totalAmount - supplyCostTotal;

                this.taxInvoiceConfig.totalAmount = totalAmount;
                this.taxInvoiceConfig.supplyCostTotal = supplyCostTotal;
                this.taxInvoiceConfig.taxTotal = taxTotal;

                if (this.taxInvoiceConfig.productDetails.length > 0) {
                    const firstProduct = this.taxInvoiceConfig.productDetails[0];
                    if (firstProduct) {
                        firstProduct.unitCost = totalAmount;
                        firstProduct.supplyCost = supplyCostTotal;
                        firstProduct.tax = taxTotal;
                        firstProduct.qty = 1;

                        const originalProduct = firstProduct.originalProduct;
                        if (originalProduct) {
                            let isHomeType = false;
                            let isMobileType = false;

                            if (originalProduct.name1 && originalProduct.name1.includes('산소발생기')) {
                                if (originalProduct.name2 === '휴대용') {
                                    isMobileType = true;
                                } else if (originalProduct.name2 === '가정용' || originalProduct.name3 === '산소발생기가정용') {
                                    isHomeType = true;
                                }
                            }

                            let purchaseDate = this.formatDateForInput(new Date(this.taxInvoiceConfig.writeDate));

                            if (isHomeType) {
                                if (this.orderData?.useHomeEndDate) {
                                    purchaseDate = this.formatDateForInput(new Date(this.orderData.useHomeEndDate));
                                } else if (this.orderData?.prescriptionData?.useHomeEndDate) {
                                    purchaseDate = this.formatDateForInput(new Date(this.orderData.prescriptionData.useHomeEndDate));
                                } else if (this.orderData.taxInvoiceDate) {
                                    const taxInvoiceDate = this.orderData.taxInvoiceDate instanceof Date
                                        ? this.orderData.taxInvoiceDate
                                        : new Date(this.orderData.taxInvoiceDate);
                                    purchaseDate = this.formatDateForInput(taxInvoiceDate);
                                }
                            } else if (isMobileType) {
                                if (this.orderData?.useMobileEndDate) {
                                    purchaseDate = this.formatDateForInput(new Date(this.orderData.useMobileEndDate));
                                } else if (this.orderData?.prescriptionData?.useMobileEndDate) {
                                    purchaseDate = this.formatDateForInput(new Date(this.orderData.prescriptionData.useMobileEndDate));
                                } else if (this.orderData.taxInvoiceDate) {
                                    const taxInvoiceDate = this.orderData.taxInvoiceDate instanceof Date
                                        ? this.orderData.taxInvoiceDate
                                        : new Date(this.orderData.taxInvoiceDate);
                                    purchaseDate = this.formatDateForInput(taxInvoiceDate);
                                }
                            } else if (this.orderData.taxInvoiceDate) {
                                const taxInvoiceDate = this.orderData.taxInvoiceDate instanceof Date
                                    ? this.orderData.taxInvoiceDate
                                    : new Date(this.orderData.taxInvoiceDate);
                                purchaseDate = this.formatDateForInput(taxInvoiceDate);
                            }

                            firstProduct.purchaseDT = purchaseDate;
                        } else if (this.orderData.taxInvoiceDate) {
                            const taxInvoiceDate = this.orderData.taxInvoiceDate instanceof Date
                                ? this.orderData.taxInvoiceDate
                                : new Date(this.orderData.taxInvoiceDate);
                            firstProduct.purchaseDT = this.formatDateForInput(taxInvoiceDate);
                        }
                    }
                    this.updateCalculatedAmounts();
                }
            } else {
                this.updateCalculatedAmounts();
            }

            // 현금영수증 금액 설정
            if (this.orderData.cashReceiptAmount) {
                const totalAmount = this.parseAmount(this.orderData.cashReceiptAmount);
                const supplyCostTotal = Math.round(totalAmount / 1.1);
                const taxTotal = totalAmount - supplyCostTotal;

                this.cashReceiptConfig.totalAmount = totalAmount;
                this.cashReceiptConfig.supplyCost = supplyCostTotal;
                this.cashReceiptConfig.tax = taxTotal;
            } else {
                const calculated = this.calculatedCashReceiptAmounts;
                this.cashReceiptConfig.totalAmount = calculated.totalAmount;
                this.cashReceiptConfig.supplyCost = calculated.supplyCostTotal;
                this.cashReceiptConfig.tax = calculated.taxTotal;
            }
        },

        // 계산된 금액 업데이트
        updateCalculatedAmounts() {
            let supplyCostTotal = 0;
            let taxTotal = 0;
            let totalAmount = 0;

            this.taxInvoiceConfig.productDetails.forEach(product => {
                // 품목별로 이미 계산된 supplyCost와 tax를 직접 사용
                const productSupplyCost = product.supplyCost || 0;
                const productTax = product.tax || 0;
                const productTotalAmount = productSupplyCost + productTax;

                supplyCostTotal += productSupplyCost;
                taxTotal += productTax;
                totalAmount += productTotalAmount;
            });

            this.taxInvoiceConfig.supplyCostTotal = supplyCostTotal;
            this.taxInvoiceConfig.taxTotal = taxTotal;
            this.taxInvoiceConfig.totalAmount = totalAmount;
        },

        toggleHistory() {
            this.isHistoryExpanded = !this.isHistoryExpanded;
        },

        // 공급받는자 라벨 텍스트 반환
        getInvoiceeLabel() {
            switch (this.taxInvoiceConfig.invoiceeType) {
                case '사업자':
                    return '공급받는자 사업자번호';
                case '개인':
                    return '공급받는자 주민번호';
                case '외국인':
                    return '공급받는자 등록번호';
                default:
                    return '등록번호';
            }
        },

        // 공급받는자 placeholder 텍스트 반환
        getInvoiceePlaceholder() {
            switch (this.taxInvoiceConfig.invoiceeType) {
                case '사업자':
                    return '사업자번호를 입력하세요';
                case '개인':
                    return '주민번호를 입력하세요';
                case '외국인':
                    return '9999999999999';
                default:
                    return '등록번호를 입력하세요';
            }
        },

        async issueTaxInvoice() {
            this.isProcessing = true;
            this.activeDocument = 'taxInvoice';
            this.errorMessage = '';

            try {
                this.taxInvoiceStatus = 'issuing';
                this.updateCalculatedAmounts();

                const taxInvoiceData = {
                    issueType: this.taxInvoiceConfig.issueType,
                    taxType: this.taxInvoiceConfig.taxType,
                    chargeDirection: this.taxInvoiceConfig.chargeDirection,
                    writeDate: this.formatDateForTaxInvoice(new Date(this.taxInvoiceConfig.writeDate)),
                    purposeType: this.taxInvoiceConfig.purposeType,
                    supplyCostTotal: this.taxInvoiceConfig.supplyCostTotal,
                    taxTotal: this.taxInvoiceConfig.taxTotal,
                    totalAmount: this.taxInvoiceConfig.totalAmount,
                    invoicerMgtKey: `TI${Date.now()}`,
                    invoicerCorpNum: this.taxInvoiceConfig.invoicerCorpNum,
                    invoicerCorpName: this.taxInvoiceConfig.invoicerCorpName,
                    invoicerCEOName: this.taxInvoiceConfig.invoicerCEOName,
                    invoicerAddr: this.taxInvoiceConfig.invoicerAddr,
                    invoicerBizType: this.taxInvoiceConfig.invoicerBizType,
                    invoicerBizClass: this.taxInvoiceConfig.invoicerBizClass,
                    invoicerEmail: this.taxInvoiceConfig.invoicerEmail,
                    invoiceeType: this.taxInvoiceConfig.invoiceeType,
                    invoiceeCorpNum: this.taxInvoiceConfig.invoiceeCorpNum,
                    invoiceeCorpName: this.taxInvoiceConfig.invoiceeCorpName,
                    invoiceeCEOName: this.taxInvoiceConfig.invoiceeCEOName,
                    detailList: this.taxInvoiceConfig.productDetails.map((product, index) => {
                        const quantity = parseInt(product.qty) || 1;
                        const unitPrice = parseFloat(product.unitCost) || 0;
                        const totalPrice = quantity * unitPrice;
                        const supplyCost = Math.round(totalPrice / 1.1);
                        const tax = totalPrice - supplyCost;

                        return {
                            serialNum: index + 1,
                            purchaseDT: this.formatDateForTaxInvoice(new Date(product.purchaseDT)),
                            itemName: product.itemName,
                            qty: quantity.toString(),
                            unitCost: Math.round(unitPrice).toString(),
                            supplyCost: supplyCost.toString(),
                            tax: tax.toString(),
                        };
                    })
                };

                const response = await this.$axios.post(`${this.$apiUrls.hanqmedi}/popbill/taxinvoice/registIssueTaxInvoice`, {
                    taxInvoiceData: taxInvoiceData,
                    CorpNum: taxInvoiceData.invoicerCorpNum,
                    UserID: this.$globalState.popbill_id
                });

                // issueTaxInvoice 메서드 수정 (1439줄 이후)
                if (response.data && response.data.code === 1) {
                    this.taxInvoiceStatus = 'issued';

                    // 발행 응답에서 바로 승인번호와 문서번호 추출
                    const issueResponse = response.data.data || response.data;

                    // 필드명 대소문자 확인: 응답에는 ntsConfirmNum (대문자 C)로 오므로 두 가지 모두 확인
                    const ntsconfirmNum = issueResponse.ntsConfirmNum ||
                        issueResponse.ntsconfirmNum ||
                        '';
                    const actualMgtKey = issueResponse.mgtKey ||
                        issueResponse.invoicerMgtKey ||
                        taxInvoiceData.invoicerMgtKey;

                    console.log('발행 응답:', issueResponse);
                    console.log('국세청 승인번호 (ntsConfirmNum):', issueResponse.ntsConfirmNum);
                    console.log('국세청 승인번호 (ntsconfirmNum):', issueResponse.ntsconfirmNum);
                    console.log('국세청 승인번호 (최종):', ntsconfirmNum);
                    console.log('문서번호:', actualMgtKey);

                    // 발행일자 설정
                    let writeDate = this.formatDateForInput(new Date(this.taxInvoiceConfig.writeDate));
                    if (issueResponse.writeDate) {
                        const dateStr = issueResponse.writeDate.toString();
                        if (dateStr.length === 8) {
                            writeDate = this.formatDateFromString(dateStr);
                        } else {
                            try {
                                const dateObj = new Date(issueResponse.writeDate);
                                if (!isNaN(dateObj.getTime())) {
                                    writeDate = this.formatDateForInput(dateObj);
                                }
                            } catch (e) {
                                console.warn('날짜 포맷 변환 실패:', e);
                            }
                        }
                    }

                    // 금액 설정
                    const totalAmount = issueResponse.totalAmount || this.taxInvoiceConfig.totalAmount;
                    const amountString = typeof totalAmount === 'number' ? totalAmount.toString() :
                        (typeof totalAmount === 'string' ? totalAmount.replace(/,/g, '') : '0');

                    // 발행 응답에 승인번호가 있으면 바로 저장
                    if (ntsconfirmNum) {
                        // 발행 응답에서 바로 승인번호를 받았으므로 바로 저장
                        await this.updateClaimDocumentInfo(this.orderId, this.claimType, {
                            taxInvoice: ntsconfirmNum,
                            taxInvoiceDate: writeDate,
                            taxInvoiceAmount: amountString,
                            taxInvoiceMgtKey: actualMgtKey
                        });

                        // 발행 내역 업데이트
                        const historyData = {
                            ...issueResponse,
                            mgtKey: actualMgtKey,
                            ntsconfirmNum: ntsconfirmNum,
                            writeDate: writeDate, // 발행일자 추가
                            supplyCostTotal: this.taxInvoiceConfig.supplyCostTotal, // 공급가액 합계 추가
                            taxTotal: this.taxInvoiceConfig.taxTotal, // 세액 합계 추가
                            totalAmount: this.taxInvoiceConfig.totalAmount, // 합계금액 추가
                            issueType: this.taxInvoiceConfig.issueType, // 발행유형 추가
                            taxType: this.taxInvoiceConfig.taxType, // 과세형태 추가
                            invoicerCorpNum: this.taxInvoiceConfig.invoicerCorpNum, // 공급자 사업자번호 추가
                            invoicerCorpName: this.taxInvoiceConfig.invoicerCorpName, // 공급자 상호 추가
                            invoiceeType: this.taxInvoiceConfig.invoiceeType, // 공급받는자 유형 추가
                            invoiceeCorpName: this.taxInvoiceConfig.invoiceeCorpName, // 공급받는자 상호 추가
                            invoiceeCorpNum: this.taxInvoiceConfig.invoiceeCorpNum // 공급받는자 등록번호 추가
                        };
                        this.updateTaxInvoiceHistory(historyData);

                        // 국세청 즉시 전송
                        try {
                            await new Promise(resolve => setTimeout(resolve, 1000));

                            await this.$axios.post(`${this.$apiUrls.hanqmedi}/popbill/taxinvoice/sendToNTS`, {
                                mgtKey: actualMgtKey,
                                CorpNum: this.taxInvoiceConfig.invoicerCorpNum,
                                UserID: this.$globalState.popbill_id,
                                memo: '국세청 즉시 전송'
                            });
                            console.log('국세청 즉시 전송 성공 (문서번호:', actualMgtKey, ')');
                        } catch (ntsError) {
                            console.error('국세청 즉시 전송 실패:', ntsError);
                            this.$swal.fire({
                                icon: 'warning',
                                title: '국세청 전송 실패',
                                text: `전자세금계산서는 발행되었으나 국세청 전송에 실패했습니다. (문서번호: ${actualMgtKey}) 나중에 다시 시도해주세요.`,
                                confirmButtonText: '확인'
                            });
                        }
                    } else {
                        // 발행 응답에 승인번호가 없으면 상세정보 조회 시도
                        try {
                            // 팝빌 서버 반영 대기 (1초)
                            await new Promise(resolve => setTimeout(resolve, 1000));

                            const detailResponse = await this.$axios.get(`${this.$apiUrls.hanqmedi}/popbill/taxinvoice/getDetailInfo`, {
                                params: {
                                    mgtKey: actualMgtKey,
                                    CorpNum: this.taxInvoiceConfig.invoicerCorpNum,
                                    UserID: this.$globalState.popbill_id
                                }
                            });

                            if (detailResponse.data && detailResponse.data.code === 1) {
                                const responseData = detailResponse.data.data || detailResponse.data;
                                const detailNtsconfirmNum = responseData.ntsconfirmNum || '';

                                // 상세정보에서 승인번호와 날짜 업데이트
                                if (responseData.writeDate) {
                                    const dateStr = responseData.writeDate.toString();
                                    if (dateStr.length === 8) {
                                        writeDate = this.formatDateFromString(dateStr);
                                    } else {
                                        try {
                                            const dateObj = new Date(responseData.writeDate);
                                            if (!isNaN(dateObj.getTime())) {
                                                writeDate = this.formatDateForInput(dateObj);
                                            }
                                        } catch (e) {
                                            console.warn('날짜 포맷 변환 실패:', e);
                                        }
                                    }
                                }

                                const detailTotalAmount = responseData.totalAmount || totalAmount;
                                const detailAmountString = typeof detailTotalAmount === 'number' ? detailTotalAmount.toString() :
                                    (typeof detailTotalAmount === 'string' ? detailTotalAmount.replace(/,/g, '') : amountString);

                                await this.updateClaimDocumentInfo(this.orderId, this.claimType, {
                                    taxInvoice: detailNtsconfirmNum,
                                    taxInvoiceDate: writeDate,
                                    taxInvoiceAmount: detailAmountString,
                                    taxInvoiceMgtKey: actualMgtKey
                                });

                                // 발행 내역 업데이트
                                const historyData = {
                                    ...responseData,
                                    mgtKey: actualMgtKey,
                                    ntsconfirmNum: detailNtsconfirmNum,
                                    writeDate: writeDate, // 발행일자 추가
                                    supplyCostTotal: responseData.supplyCostTotal || this.taxInvoiceConfig.supplyCostTotal, // 공급가액 합계 추가
                                    taxTotal: responseData.taxTotal || this.taxInvoiceConfig.taxTotal, // 세액 합계 추가
                                    totalAmount: responseData.totalAmount || this.taxInvoiceConfig.totalAmount, // 합계금액 추가
                                    issueType: responseData.issueType || this.taxInvoiceConfig.issueType, // 발행유형 추가
                                    taxType: responseData.taxType || this.taxInvoiceConfig.taxType, // 과세형태 추가
                                    invoicerCorpNum: responseData.invoicerCorpNum || this.taxInvoiceConfig.invoicerCorpNum, // 공급자 사업자번호 추가
                                    invoicerCorpName: responseData.invoicerCorpName || this.taxInvoiceConfig.invoicerCorpName, // 공급자 상호 추가
                                    invoiceeType: responseData.invoiceeType || this.taxInvoiceConfig.invoiceeType, // 공급받는자 유형 추가
                                    invoiceeCorpName: responseData.invoiceeCorpName || this.taxInvoiceConfig.invoiceeCorpName, // 공급받는자 상호 추가
                                    invoiceeCorpNum: responseData.invoiceeCorpNum || this.taxInvoiceConfig.invoiceeCorpNum // 공급받는자 등록번호 추가
                                };
                                this.updateTaxInvoiceHistory(historyData);
                            } else {
                                // 상세정보 조회 실패 시 문서번호만 저장
                                await this.updateClaimDocumentInfo(this.orderId, this.claimType, {
                                    taxInvoice: '',
                                    taxInvoiceDate: writeDate,
                                    taxInvoiceAmount: amountString,
                                    taxInvoiceMgtKey: actualMgtKey
                                });
                                this.updateTaxInvoiceHistory();
                            }
                        } catch (error) {
                            console.error('상세정보 조회 실패:', error);

                            // 상세정보 조회 실패 시 문서번호만 저장
                            await this.updateClaimDocumentInfo(this.orderId, this.claimType, {
                                taxInvoice: '',
                                taxInvoiceDate: writeDate,
                                taxInvoiceAmount: amountString,
                                taxInvoiceMgtKey: actualMgtKey
                            });
                            this.updateTaxInvoiceHistory();
                        }
                    }

                    this.$emit('document-issued', { type: 'taxInvoice', status: 'issued' });

                    this.$swal.fire({
                        icon: 'success',
                        title: '발행 완료',
                        text: '전자세금계산서가 성공적으로 발행되었습니다.',
                        confirmButtonText: '확인'
                    });
                } else {
                    throw new Error(response.data.message || '전자세금계산서 발행 실패');
                }

            } catch (error) {
                this.taxInvoiceStatus = 'error';
                this.errorMessage = error.message || '전자세금계산서 발행 중 오류가 발생했습니다.';

                this.$swal.fire({
                    icon: 'error',
                    title: '발행 실패',
                    text: this.errorMessage,
                    confirmButtonText: '확인'
                });
            } finally {
                this.isProcessing = false;
                this.activeDocument = null;
            }
        },

        async issueCashReceipt() {
            this.isProcessing = true;
            this.activeDocument = 'cashReceipt';
            this.errorMessage = '';

            try {
                this.cashReceiptStatus = 'issuing';

                const cashReceiptData = {
                    mgtKey: `CR${Date.now()}`,
                    tradeType: this.cashReceiptConfig.tradeType,
                    tradeUsage: this.cashReceiptConfig.tradeUsage,
                    tradeOpt: '일반',
                    taxationType: this.cashReceiptConfig.taxationType,
                    totalAmount: this.cashReceiptConfig.totalAmount ?
                        this.cashReceiptConfig.totalAmount.toString() :
                        this.calculatedCashReceiptAmounts.totalAmount.toString(),
                    supplyCost: this.cashReceiptConfig.supplyCost ?
                        this.cashReceiptConfig.supplyCost.toString() :
                        this.calculatedCashReceiptAmounts.supplyCostTotal.toString(),
                    tax: this.cashReceiptConfig.tax ?
                        this.cashReceiptConfig.tax.toString() :
                        this.calculatedCashReceiptAmounts.taxTotal.toString(),
                    serviceFee: '0',
                    franchiseCorpNum: this.taxInvoiceConfig.invoicerCorpNum,
                    identityNum: this.cashReceiptConfig.identityNum
                };

                const response = await this.$axios.post(`${this.$apiUrls.hanqmedi}/popbill/cashbill/registIssueCashReceipt`, {
                    cashReceiptData: cashReceiptData,
                    CorpNum: this.taxInvoiceConfig.invoicerCorpNum,
                    UserID: this.$globalState.popbill_id
                });

                if (response.data && response.data.code === 1) {
                    this.cashReceiptStatus = 'issued';

                    // 상세정보 조회 및 저장
                    try {
                        const detailResponse = await this.$axios.get(`${this.$apiUrls.hanqmedi}/popbill/cashbill/getDetailInfo`, {
                            params: {
                                mgtKey: cashReceiptData.mgtKey,
                                CorpNum: this.taxInvoiceConfig.invoicerCorpNum,
                                UserID: this.$globalState.popbill_id
                            }
                        });

                        if (detailResponse.data && detailResponse.data.mgtKey) {
                            // 실제 문서번호 확인
                            const actualMgtKey = detailResponse.data.mgtKey || cashReceiptData.mgtKey;

                            console.log('팝빌 등록 현금영수증 문서번호:', actualMgtKey);

                            let tradeDate = this.formatDateForInput(new Date());
                            if (detailResponse.data.tradeDT) {
                                const dateStr = detailResponse.data.tradeDT.toString();
                                if (dateStr.length >= 8) {
                                    tradeDate = this.formatDateFromString(dateStr.substring(0, 8));
                                }
                            }

                            const totalAmount = detailResponse.data.totalAmount || cashReceiptData.totalAmount;
                            const amountString = typeof totalAmount === 'number' ? totalAmount.toString() :
                                (typeof totalAmount === 'string' ? totalAmount.replace(/,/g, '') : '0');

                            // DB에 승인번호, 발행일자, 금액, 문서번호 저장
                            await this.updateClaimDocumentInfo(this.orderId, this.claimType, {
                                cashReceipt: detailResponse.data.confirmNum || '',
                                cashReceiptDate: tradeDate,
                                cashReceiptAmount: amountString,
                                cashReceiptMgtKey: actualMgtKey // 문서번호 저장
                            });

                            // 발행 내역 업데이트
                            this.updateCashReceiptHistory(detailResponse.data);
                        } else {
                            await this.updateClaimDocumentInfo(this.orderId, this.claimType, {
                                cashReceipt: '',
                                cashReceiptDate: this.formatDateForInput(new Date()),
                                cashReceiptAmount: cashReceiptData.totalAmount.toString()
                            });
                            this.updateCashReceiptHistory();
                        }
                    } catch (error) {
                        console.error('현금영수증 상세정보 조회 및 저장 실패:', error);
                        try {
                            await this.updateClaimDocumentInfo(this.orderId, this.claimType, {
                                cashReceipt: '',
                                cashReceiptDate: this.formatDateForInput(new Date()),
                                cashReceiptAmount: cashReceiptData.totalAmount.toString()
                            });
                        } catch (saveError) {
                            console.error('문서 정보 저장 실패:', saveError);
                        }
                        this.updateCashReceiptHistory();
                    }

                    this.$emit('document-issued', { type: 'cashReceipt', status: 'issued' });

                    this.$swal.fire({
                        icon: 'success',
                        title: '발행 완료',
                        text: '현금영수증이 성공적으로 발행되었습니다.',
                        confirmButtonText: '확인'
                    });
                } else {
                    throw new Error(response.data.message || '현금영수증 발행 실패');
                }

            } catch (error) {
                this.cashReceiptStatus = 'error';
                this.errorMessage = error.message || '현금영수증 발행 중 오류가 발생했습니다.';

                this.$swal.fire({
                    icon: 'error',
                    title: '발행 실패',
                    text: this.errorMessage,
                    confirmButtonText: '확인'
                });
            } finally {
                this.isProcessing = false;
                this.activeDocument = null;
            }
        },

        async updateClaimDocumentInfo(orderId, claimType, documentInfo) {
            try {
                await this.$axios.put(`${this.$apiUrls.hanqmedi}/updateClaimDocumentInfo`, {
                    orderId,
                    claimType,
                    documentInfo
                });
            } catch (error) {
                console.error('문서 정보 업데이트 실패:', error);
            }
        },

        // 세금계산서 문서함 팝업 열기
        async openTaxInvoicePopup() {
            try {
                // 팝빌 API를 통해 세금계산서 문서함 URL 조회
                const response = await this.$axios.get(`${this.$apiUrls.hanqmedi}/popbill/taxinvoice/getURL`, {
                    params: {
                        CorpNum: this.popbillCorpNum,
                        UserID: this.popbillUserId,
                        Togo: 'TBOX' // 임시 문서함
                    }
                });

                if (response.data && response.data.url) {
                    const popup = window.open(
                        response.data.url,
                        'taxInvoicePopup',
                        'width=1200,height=800,scrollbars=yes,resizable=yes,toolbar=no,menubar=no,location=no,status=no'
                    );

                    if (popup) {
                        popup.focus();
                    } else {
                        this.$swal.fire({
                            icon: 'warning',
                            title: '팝업 차단 안내',
                            text: '팝업이 차단되었습니다. 팝업 차단을 해제해주세요.',
                            confirmButtonText: '확인'
                        });
                    }
                } else {
                    throw new Error('문서함 URL을 가져올 수 없습니다.');
                }
            } catch (error) {
                console.error('세금계산서 문서함 열기 실패:', error);
                this.$swal.fire({
                    icon: 'error',
                    title: '실패',
                    text: '세금계산서 문서함을 열 수 없습니다.',
                    confirmButtonText: '확인'
                });
            }
        },

        // 현금영수증 문서함 팝업 열기
        async openCashReceiptPopup() {
            try {
                // 팝빌 API를 통해 현금영수증 문서함 URL 조회
                const response = await this.$axios.get(`${this.$apiUrls.hanqmedi}/popbill/cashbill/getURL`, {
                    params: {
                        CorpNum: this.popbillCorpNum,
                        UserID: this.popbillUserId,
                        Togo: 'TBOX' // 임시 문서함
                    }
                });

                if (response.data && response.data.url) {
                    const popup = window.open(
                        response.data.url,
                        'cashReceiptPopup',
                        'width=1280,height=800,scrollbars=yes,resizable=yes,toolbar=no,menubar=no,location=no,status=no'
                    );

                    if (popup) {
                        popup.focus();
                    } else {
                        this.$swal.fire({
                            icon: 'warning',
                            title: '팝업 차단 안내',
                            text: '팝업이 차단되었습니다. 팝업 차단을 해제해주세요.',
                            confirmButtonText: '확인'
                        });
                    }
                } else {
                    throw new Error('문서함 URL을 가져올 수 없습니다.');
                }
            } catch (error) {
                console.error('현금영수증 문서함 열기 실패:', error);
                this.$swal.fire({
                    icon: 'error',
                    title: '실패',
                    text: '현금영수증 문서함을 열 수 없습니다.',
                    confirmButtonText: '확인'
                });
            }
        },

        formatDateTime(date) {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            return `${year}-${month}-${day} ${hours}:${minutes}`;
        },

        // 현금영수증용 거래일시 포맷팅 (yyyyMMddHHmmss -> yyyy-MM-dd HH:mm:ss)
        formatDateTimeFromString(dateTimeString) {
            if (!dateTimeString || dateTimeString.length !== 14) return '-';
            const year = dateTimeString.substring(0, 4);
            const month = dateTimeString.substring(4, 6);
            const day = dateTimeString.substring(6, 8);
            return `${year}-${month}-${day}`;
        },

        // 현금영수증용 거래일시 포맷팅 (yyyyMMddHHmmss)
        formatDateTimeForCashReceipt(date) {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            const seconds = String(date.getSeconds()).padStart(2, '0');
            return `${year}${month}${day}${hours}${minutes}${seconds}`;
        },

        formatDateForTaxInvoice(date) {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}${month}${day}`;
        },

        formatDateForInput(date) {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        },

        // 날짜 포맷팅 (yyyyMMdd -> yyyy-MM-dd)
        formatDateFromString(dateString) {
            if (!dateString || dateString.length !== 8) return '-';
            const year = dateString.substring(0, 4);
            const month = dateString.substring(4, 6);
            const day = dateString.substring(6, 8);
            return `${year}-${month}-${day}`;
        },

        formatNumber(number) {
            return new Intl.NumberFormat('ko-KR').format(number);
        },

        getTaxInvoiceButtonText() {
            if (this.isProcessing && this.activeDocument === 'taxInvoice') return '발행 중...';
            if (this.taxInvoiceStatus === 'issued') return '발행 완료';
            return '발행';
        },

        // 문자열에서 숫자 추출 (쉼표 제거)
        parseAmount(amountString) {
            if (!amountString) return 0;
            const cleaned = String(amountString).replace(/,/g, '').trim();
            return parseFloat(cleaned) || 0;
        },

        getCashReceiptButtonText() {
            if (this.isProcessing && this.activeDocument === 'cashReceipt') return '발행 중...';
            if (this.cashReceiptStatus === 'issued') return '발행 완료';
            return '발행';
        },
    },

    watch: {
        orderData: {
            handler(newOrderData) {
                if (newOrderData) {
                    this.initializeOrderData();
                }
            },
            immediate: true,
            deep: true
        },

        'taxInvoiceConfig.invoiceeType'(newType) {
            if (newType === '외국인') {
                this.taxInvoiceConfig.invoiceeCorpNum = '9999999999999';
            } else {
                this.taxInvoiceConfig.invoiceeCorpNum = '';
            }
        },

        'taxInvoiceConfig.productDetails': {
            handler() {
                this.updateCalculatedAmounts();
            },
            deep: true
        },

        'calculatedCashReceiptAmounts': {
            handler(newAmounts) {
                if (this.cashReceiptConfig.totalAmount === 0 ||
                    (this.cashReceiptConfig.totalAmount === newAmounts.totalAmount)) {
                    this.cashReceiptConfig.totalAmount = newAmounts.totalAmount;
                    this.cashReceiptConfig.supplyCost = newAmounts.supplyCostTotal;
                    this.cashReceiptConfig.tax = newAmounts.taxTotal;
                }
            },
            immediate: true
        },
    }
}
</script>